import { createStorageKey, local, setLocalStorageItem } from '@utils/storage';
import { StorageKeys } from '@constants/storageKeys';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '@js/authConfig';
import log, { LogContext } from '@utils/logging';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { buildVersion } from '@constants/buildVersion';
import { executePostRequest } from '@api/client';
import { AuthenticateResponse } from '@models/authenticate/Authenticate';
import { createAuthenticateApiPath } from '@routing/apiUrls';
import { DecodedToken } from '@models/DecodedToken';
import { jwtDecode } from 'jwt-decode';
import { PagePath } from '@routing/appUrls';
import { useAtom } from 'jotai';
import { AuthenticationState, authenticationStateAtom } from '@js/store/authenticationAtoms';

const useAuthentication = () => {
    const { instance, inProgress } = useMsal();
    const [authenticationState, setAuthenticationState] = useAtom(authenticationStateAtom);

    const decodeAccessToken = (accessToken: string): DecodedToken => {
        return jwtDecode(accessToken) as DecodedToken;
    };

    const azureLogin = (from?: string) => {
        if (inProgress !== InteractionStatus.None) {
            console.error('Login in progress. Return?');
            // return;
        }

        instance
            .loginRedirect(
                from
                    ? {
                          ...loginRequest,
                          state: from,
                      }
                    : loginRequest,
            )
            .then(async () => {
                log({
                    context: LogContext.User,
                    title: 'Logged in',
                    reason: 'The user logged in',
                });
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const azureLogout = () => {
        instance.logoutPopup().then(() => {
            log({
                context: LogContext.User,
                title: 'Logged out',
                reason: 'The user has logged out',
            });
            clearAccessToken(AuthenticationState.LoggedOut);
        });
    };

    const storeApiBaseUrl = (token: string) => {
        const decodedToken = decodeAccessToken(token);

        if (!decodedToken?.enrichurl) {
            return;
        }

        log({
            context: LogContext.User,
            title: 'Stored API base url',
            reason: 'Token changed',
            data: {
                enrichurl: decodedToken.enrichurl,
            },
        });

        setLocalStorageItem(createStorageKey([StorageKeys.ApiBaseUrl]), decodedToken.enrichurl);
    };

    const getAzureToken = async () => {
        const activeAccount = instance.getActiveAccount();
        const accounts = instance.getAllAccounts();
        const account = activeAccount || accounts[0];

        if (!account) {
            window.location.href = `${window.location.origin}${PagePath.login}`;
            return;
        }

        const accessTokenRequest = {
            ...loginRequest,
            // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/1340#issuecomment-595381621
            redirectUri: '/auth.html',
            account,
        };

        return instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => accessTokenResponse.accessToken)
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance
                        .acquireTokenPopup(accessTokenRequest)
                        .then((accessTokenResponse) => accessTokenResponse.accessToken)
                        .catch(() => {
                            window.location.href = `${window.location.origin}${PagePath.login}`;
                            return;
                        });
                } else {
                    window.location.href = `${window.location.origin}${PagePath.login}`;
                    return;
                }
            });
    };

    const storeAccessToken = (logReason: string, token: string): void => {
        log({
            context: LogContext.User,
            title: 'Stored access token',
            reason: logReason,
            data: {
                accessToken: token,
            },
        });

        local.setItem(StorageKeys.AccessToken, token);
        setAuthenticationState(undefined);
        storeApiBaseUrl(token);
    };

    const getAccessToken = () => {
        return local.getItem(StorageKeys.AccessToken);
    };

    const getDecodedAccessToken = () => {
        const accessToken = getAccessToken();
        return accessToken ? decodeAccessToken(accessToken) : null;
    };

    const refreshAccessToken = async (): Promise<undefined | string> => {
        const activeAccount = instance.getActiveAccount();
        const accounts = instance.getAllAccounts();
        const currentAccount = activeAccount || accounts[0];

        const azureToken = await getAzureToken();
        const tenantId = local.getItem(StorageKeys.TenantId);

        if (!azureToken || !tenantId) {
            return;
        }

        const result = await executePostRequest<AuthenticateResponse>(createAuthenticateApiPath(), {
            azureToken,
            emailAddress: currentAccount.username,
            tenantId,
            clientVersion: buildVersion,
        });

        const newToken = result.data.token;

        if (!newToken) {
            clearAccessToken(AuthenticationState.SessionExpired);
            return;
        }

        storeAccessToken('Token refreshed', newToken);
        return newToken;
    };

    const clearAccessToken = (authenticationState: AuthenticationState): void => {
        log({
            context: LogContext.User,
            title: 'Cleared access token',
            reason: authenticationState,
        });
        setAuthenticationState(authenticationState);
        local.removeItem(StorageKeys.AccessToken);
    };

    return {
        azureLogin,
        azureLogout,
        getAzureToken,
        storeAccessToken,
        getAccessToken,
        refreshAccessToken,
        clearAccessToken,
        decodeAccessToken,
        getDecodedAccessToken,
        authenticationState,
    };
};

export default useAuthentication;
